import dynamic from 'next/dynamic';
import React, {
  type ComponentType,
  type ReactNode,
  useEffect,
  type JSX,
} from 'react';
import { type GameViewModel } from '../../../resources/js/typings/viewModels/gameViewModel';
import { GameContextProvider } from '../../../resources/js/contexts/useGame';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import getBuildsSpecPageViewModels, {
  type BuildsSpecPageViewModel,
} from '../../helpers/getBuildsSpecPageViewModels';
import getBuildsSpecPageSlugs, {
  type BuildsSpecPageSlugsViewModel,
} from '../../helpers/getBuildsSpecPageSlugs';
import configureSentryScope from '../../helpers/configureSentryScope';
import Seo from '../../components/Seo/Seo';
import VerticalContent from '../../../resources/js/components/VerticalContent/VerticalContent';
import EnhancedMarkdown from '../../../resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import TabbedSelectLinkOptionWrapper from '../../components/TabbedSelectLinkOptionWrapper/TabbedSelectLinkOptionWrapper';
import TabbedSelect from '../../../resources/js/components/TabbedSelect/TabbedSelect';
import HorizontalContent from '../../../resources/js/components/HorizontalContent/HorizontalContent';
import BuildsMetadataSummary from '../../../resources/js/components/BuildsMetadataSummary/BuildsMetadataSummary';
import Select from '../../../resources/js/components/Select/Select';
import SelectLinkOptionWrapper from '../../components/SelectLinkOptionWrapper/SelectLinkOptionWrapper';
import Image from 'next/image';
import BuildsSectionComponent from '../../../resources/js/components/BuildsSectionComponent/BuildsSectionComponent';
import SpecSelect from '../../../resources/js/components/SpecSelect/SpecSelect';
import Heading from '../../../resources/js/components/Heading/Heading';
import Body from '../../../resources/js/components/Body/Body';
import styles from './builds.module.scss';
import PageBackground from '../../components/PageBackground/PageBackground';
import { type ArchonHeaderViewModel } from '../../components/ArchonHeader/ArchonHeader';
import { type ArchonFooterViewModel } from '../../components/ArchonFooter/ArchonFooter';
import ArchonLayout, {
  Container,
} from '../../layouts/ArchonLayout/ArchonLayout';
import config from '../../config';
import { TableHiddenColumnsProvider } from '@resources/js/contexts/useTableHiddenColumns';
import BuildsAdvancedSettings from '@resources/js/components/BuildsAdvancedSettings/BuildsAdvancedSettings';
import { sendEvent } from '@resources/js/helpers/analytics';
import unique from '@resources/js/helpers/unique';
import LinkButton from '@resources/js/components/LinkButton/LinkButton';
import Link from 'next/link';
import { EnhancedMarkdownOptionsContextProvider } from '@resources/js/contexts/useEnhancedMarkdownOptions';
import { initRequest } from '../../helpers/rpgLogsApiRequestMetadata';
import { TalentTreeBlueprintContextProvider } from '@resources/js/contexts/useTalentTreeBlueprint';
import { V3CompactTheme } from '@resources/js/contexts/useTheme';
import { BuildsRotationFacetsContextProvider } from '@resources/js/contexts/useBuildsRotationFacets';
import Fullscreenable from '../../components/Fullscreenable/Fullscreenable';
import { MobileCategorySelectLinkOptionWrapper } from '../../components/MobileCategorySelectLinkOptionWrapper/MobileCategorySelectLinkOptionWrapper';

type BuildsSpecPageProps = {
  game: GameViewModel;
  header: ArchonHeaderViewModel;
  footer: ArchonFooterViewModel;
  page: BuildsSpecPageViewModel & { imageBlurDataUrl: string | null };
};

const enhancedMarkdownOptions = {
  lazyLoad: false,
};

const BuildsOpenerSection = dynamic(
  () =>
    import('@resources/js/components/BuildsOpenerSection/BuildsOpenerSection'),
  {
    ssr: false,
  },
);
const BuildsCooldownsSection = dynamic(
  () =>
    import('@resources/js/components/BuildsCooldownsSection/BuildsCooldownsSection'),
  {
    ssr: false,
  },
);

const buildsSectionOverrides: Record<string, ComponentType<never>> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  BuildsOpenerSection: (props: any) => (
    <BuildsOpenerSection {...props} Wrapper={Fullscreenable} />
  ),

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  BuildsCooldownsSection: (props: any) => (
    <BuildsCooldownsSection {...props} Wrapper={Fullscreenable} />
  ),
};

export function BuildsSpecPage({
  game,
  header,
  footer,
  page,
}: BuildsSpecPageProps): JSX.Element {
  useEffect(() => {
    sendLoadEvent({
      gameSlug: game.archonGameSlug,
      specId: page.selectedSpec,
      zoneTypeSlug: page.selectedZoneType,
      categorySlug: page.selectedCategory,
      difficultySlug: page.selectedDifficulty,
      encounterSlug: page.selectedEncounter,
      affixesSlug: page.selectedAffixes,
    });
  }, [game, page]);

  return (
    <GameContextProvider game={game}>
      <ArchonLayout
        header={header}
        breadcrumbs={page.breadcrumbs}
        footer={footer}
        theme={V3CompactTheme}
      >
        <Seo {...page.seo} game={game} />
        {page.imageUrl && (
          <PageBackground
            imageUrl={page.imageUrl}
            width={989}
            height={564}
            withBreadcrumbs={page.breadcrumbs.length > 0}
          />
        )}
        <Container shouldShowAdsIfEnabled={true}>
          <Body>
            <section
              className={styles.filtersContainer + ' ' + styles.topElement}
            >
              <HorizontalContent
                gap={'small'}
                className={
                  'horizontal-content--stretch-wrap horizontal-content--desktop-no-wrap'
                }
              >
                <SpecSelect
                  specOptions={page.specOptions}
                  selectedSpec={page.selectedSpec}
                  classLabel={page.classLabel}
                  specLabel={page.specLabel}
                />
                {page.zoneTypeOptions.length > 1 && (
                  <VerticalContent gap={'tiny'} style={{ flexShrink: 0 }}>
                    <label>
                      <b>{page.zoneTypeLabel}</b>
                    </label>
                    <TabbedSelect
                      value={
                        page.zoneTypeOptions.find(
                          (x) => x.value === page.selectedZoneType,
                        ) ?? page.zoneTypeOptions[0]
                      }
                      options={page.zoneTypeOptions}
                      OptionWrapper={TabbedSelectLinkOptionWrapper}
                      wrapperInheritsProps
                    />
                  </VerticalContent>
                )}
                {page.showDifficultySelect &&
                  page.difficultyOptions.length > 1 && (
                    <VerticalContent gap={'tiny'} style={{ flexShrink: 0 }}>
                      <label>
                        <b>{page.difficultyLabel}</b>
                      </label>
                      <Select
                        inputId='difficulty-select'
                        value={
                          page.difficultyOptions.find(
                            (x) => x.value === page.selectedDifficulty,
                          ) ?? page.difficultyOptions[0]
                        }
                        options={page.difficultyOptions}
                        OptionWrapper={SelectLinkOptionWrapper}
                        aria-label={page.difficultyLabel}
                        usesEnhancedMarkdown={true}
                      />
                    </VerticalContent>
                  )}
                {page.encounterOptions.length > 1 ? (
                  <VerticalContent gap={'tiny'} style={{ minWidth: '130px' }}>
                    <label>
                      <b>{page.encounterLabel}</b>
                    </label>
                    <Select
                      inputId='encounter-select'
                      value={
                        page.encounterOptions.find(
                          (x) => x.value === page.selectedEncounter,
                        ) ?? page.encounterOptions[0]
                      }
                      options={page.encounterOptions}
                      OptionWrapper={SelectLinkOptionWrapper}
                      usesEnhancedMarkdown
                      aria-label={page.encounterLabel}
                    />
                  </VerticalContent>
                ) : null}
                {page.affixesOptions.length > 1 && (
                  <VerticalContent gap={'tiny'} style={{ minWidth: '130px' }}>
                    <label>
                      <b>{page.affixesLabel}</b>
                    </label>
                    <Select
                      inputId='affixes-select'
                      value={
                        page.affixesOptions.find(
                          (x) => x.value === page.selectedAffixes,
                        ) ?? page.affixesOptions[0]
                      }
                      options={page.affixesOptions}
                      usesEnhancedMarkdown
                      OptionWrapper={SelectLinkOptionWrapper}
                      aria-label={page.affixesLabel}
                    />
                  </VerticalContent>
                )}
              </HorizontalContent>
            </section>
            <section className={styles.navigationContainer}>
              <div className={styles.categorySelectContainer}>
                <TabbedSelect
                  className={styles.desktopCategorySelect}
                  style={'v2'}
                  value={
                    page.categoryOptions.find(
                      (x) => x.value === page.selectedCategory,
                    ) ?? page.categoryOptions[0]
                  }
                  options={page.categoryOptions}
                  OptionWrapper={TabbedSelectLinkOptionWrapper}
                  wrapperInheritsProps
                />
                <TabbedSelect
                  className={styles.mobileCategorySelect}
                  style={'v2'}
                  value={
                    page.categoryOptions.find(
                      (x) => x.value === page.selectedCategory,
                    ) ?? page.categoryOptions[0]
                  }
                  options={page.categoryOptions}
                  OptionWrapper={MobileCategorySelectLinkOptionWrapper}
                  wrapperInheritsProps
                />
              </div>
              <div className={styles.sectionSelect}>
                {page.sectionOptions.length > 0 && (
                  <>
                    <span className={styles.jumpToLabel}>
                      {trans('global.jump_to')}:
                    </span>
                    {page.sectionOptions.map((option) => (
                      <LinkButton
                        key={option.value}
                        href={option.url}
                        styles={['menu', 'slim']}
                      >
                        {option.label}
                      </LinkButton>
                    ))}
                  </>
                )}
              </div>
            </section>
            <VerticalContent className={'builds-page__content'} gap={'large'}>
              <section>
                <HorizontalContent
                  gap={'content-padding'}
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Heading level={1} className={'hide-on-compact'}>
                      <EnhancedMarkdown markdown={page.title} wrapped={false} />
                    </Heading>
                    <Body
                      className={`${styles.headerDescription} hide-on-compact`}
                    >
                      <EnhancedMarkdown
                        markdown={page.description}
                        wrapped={false}
                      />
                    </Body>
                    <div className={'builds--page-subtext'}>
                      <BuildsMetadataSummary {...page} />
                    </div>
                  </div>
                  {page.imageUrl && (
                    <div
                      className={`${styles.specImageContainer} hide-on-compact`}
                    >
                      <Image
                        src={page.imageUrl}
                        width={100}
                        height={100}
                        quality={75}
                        alt={''}
                        placeholder={'blur'}
                        blurDataURL={page.imageBlurDataUrl ?? undefined}
                        className={styles.specImage}
                      />
                    </div>
                  )}
                </HorizontalContent>
              </section>
              <VerticalContent gap={'large'}>
                <section>
                  <TableHiddenColumnsProvider initialValue={['dps', 'hps']}>
                    <VerticalContent gap={'larger'}>
                      <TalentTreeBlueprintContextProvider
                        preloaded={page.talentTreeBlueprints}
                      >
                        <EnhancedMarkdownOptionsContextProvider
                          value={enhancedMarkdownOptions}
                        >
                          <BuildsRotationFacetsContextProvider>
                            {page.sections.map((section) => (
                              <BuildsSectionComponent
                                key={section.component}
                                {...section}
                                LinkWrapper={LinkWrapper}
                                overrides={buildsSectionOverrides}
                              />
                            ))}
                          </BuildsRotationFacetsContextProvider>
                        </EnhancedMarkdownOptionsContextProvider>
                      </TalentTreeBlueprintContextProvider>
                      <BuildsAdvancedSettings {...page.advancedSettings} />
                    </VerticalContent>
                  </TableHiddenColumnsProvider>
                </section>
              </VerticalContent>
            </VerticalContent>
          </Body>
        </Container>
      </ArchonLayout>
    </GameContextProvider>
  );
}

function LinkWrapper({
  href,
  children,
}: {
  href: string;
  children: ReactNode;
}): JSX.Element {
  return <Link href={href}>{children}</Link>;
}

export const getSpecPageStaticPaths: GetStaticPaths = async () => {
  const allSlugs = (await getBuildsSpecPageSlugs()).filter(
    (slugs) => !slugs.affixesSlug,
  );

  const paths = [];

  const gameSlugs = allSlugs
    .map((slugs) => slugs.gameSlug)
    .filter(unique)
    .filter((x) => x !== 'eso' && x !== 'wow-forever');
  for (const gameSlug of gameSlugs) {
    const slugsForGame = allSlugs.filter(
      (slugs) => slugs.gameSlug === gameSlug,
    );
    const zoneTypeSlugs = slugsForGame
      .map((slugs) => slugs.zoneTypeSlug)
      .filter(unique);

    for (const zoneTypeSlug of zoneTypeSlugs) {
      const slugsForZoneType = slugsForGame.filter(
        (slugs) => slugs.zoneTypeSlug === zoneTypeSlug,
      );

      const difficultySlugs = slugsForZoneType
        .map((slugs) => slugs.difficultySlug)
        .filter(unique);

      // For the first difficulty of each zone of each game...
      if (difficultySlugs.length) {
        const difficultySlug = difficultySlugs[0];
        const slugsForDifficulty = slugsForZoneType.filter(
          (slugs) => slugs.difficultySlug === difficultySlug,
        );

        const encounterSlugs = slugsForDifficulty
          .map((slugs) => slugs.encounterSlug)
          .filter(unique);
        const encounterSlugsToPreBuild = config.isProduction
          ? // either pre-build all encounters
            encounterSlugs
          : // or just the overall encounter if not production/beta
            [encounterSlugs[0]];

        const getSpecKey = (slugs: BuildsSpecPageSlugsViewModel) =>
          `${slugs.classSlug}-${slugs.specSlug}`;
        const specKeys = slugsForDifficulty.map(getSpecKey).filter(unique);
        const specKeysToPrebuild = config.isProduction
          ? specKeys
          : // take a random 10% (ish) of specs
            specKeys.filter(() => Math.random() < 0.1);

        const categorySlugs = slugsForDifficulty
          .map((slugs) => slugs.categorySlug)
          .filter(unique);
        const categorySlugsToPrebuild = config.isProduction
          ? categorySlugs
          : [categorySlugs[0]];

        const slugs = slugsForDifficulty.filter(
          (slugs) =>
            encounterSlugsToPreBuild.includes(slugs.encounterSlug) &&
            specKeysToPrebuild.includes(getSpecKey(slugs)) &&
            categorySlugsToPrebuild.includes(slugs.categorySlug),
        );

        if (config.isCI) {
          console.log(
            `Pre-building ${slugs.length} spec pages for ${gameSlug} ${zoneTypeSlug} ${difficultySlug}`,
          );
        }

        for (const slugsToPreBuild of slugs) {
          paths.push({
            params: {
              gameSlug: slugsToPreBuild.gameSlug,
              specSlug: slugsToPreBuild.specSlug,
              classSlug: slugsToPreBuild.classSlug,
              zoneTypeSlug: slugsToPreBuild.zoneTypeSlug,
              categorySlug: slugsToPreBuild.categorySlug,
              difficultySlug: slugsToPreBuild.difficultySlug,
              encounterSlug: slugsToPreBuild.encounterSlug,
            },
          });
        }
      }
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getSpecPageStaticPathsWithAffixes: GetStaticPaths = async () => {
  const allSlugs = (await getBuildsSpecPageSlugs()).filter(
    (slugs) => slugs.affixesSlug,
  );

  const paths = [];

  const gameSlugs = allSlugs
    .map((slugs) => slugs.gameSlug)
    .filter(unique)
    .filter((x) => x !== 'eso' && x !== 'wow-forever');
  for (const gameSlug of gameSlugs) {
    const slugsForGame = allSlugs.filter(
      (slugs) => slugs.gameSlug === gameSlug,
    );
    const zoneTypeSlugs = slugsForGame
      .map((slugs) => slugs.zoneTypeSlug)
      .filter(unique);

    for (const zoneTypeSlug of zoneTypeSlugs) {
      const slugsForZoneType = slugsForGame.filter(
        (slugs) => slugs.zoneTypeSlug === zoneTypeSlug,
      );

      const difficultySlugs = slugsForZoneType
        .map((slugs) => slugs.difficultySlug)
        .filter(unique)
        .filter((x) => x !== 'high-keys');

      // For the first difficulty of each zone of each game...
      if (difficultySlugs.length) {
        const difficultySlug = difficultySlugs[0];
        const slugsForDifficulty = slugsForZoneType.filter(
          (slugs) => slugs.difficultySlug === difficultySlug,
        );

        const encounterSlugs = slugsForDifficulty
          .map((slugs) => slugs.encounterSlug)
          .filter(unique);

        // For zones with affixes, only pre-build the overall encounter
        const encounterSlugsToPreBuild = [encounterSlugs[0]];

        const getSpecKey = (slugs: BuildsSpecPageSlugsViewModel) =>
          `${slugs.classSlug}-${slugs.specSlug}`;
        const specKeys = slugsForDifficulty.map(getSpecKey).filter(unique);
        const specKeysToPrebuild = config.isProduction
          ? specKeys
          : // take a random 10% (ish) of specs
            specKeys.filter(() => Math.random() < 0.1);

        const affixesSlugs = slugsForDifficulty
          .map((slugs) => slugs.affixesSlug)
          .filter(unique);

        const affixesSlugsToPreBuild = config.isProduction
          ? affixesSlugs.slice(0, 3)
          : [affixesSlugs[0]];

        const slugs = slugsForDifficulty.filter(
          (slugs) =>
            encounterSlugsToPreBuild.includes(slugs.encounterSlug) &&
            specKeysToPrebuild.includes(getSpecKey(slugs)) &&
            affixesSlugsToPreBuild.includes(slugs.affixesSlug),
        );

        if (config.isCI) {
          console.log(
            `Pre-building ${slugs.length} spec pages for ${gameSlug} ${zoneTypeSlug} ${difficultySlug}`,
          );
        }

        for (const slugsToPreBuild of slugs) {
          paths.push({
            params: {
              gameSlug: slugsToPreBuild.gameSlug,
              specSlug: slugsToPreBuild.specSlug,
              classSlug: slugsToPreBuild.classSlug,
              zoneTypeSlug: slugsToPreBuild.zoneTypeSlug,
              categorySlug: slugsToPreBuild.categorySlug,
              difficultySlug: slugsToPreBuild.difficultySlug,
              encounterSlug: slugsToPreBuild.encounterSlug,
              affixesSlug: slugsToPreBuild.affixesSlug as string,
            },
          });
        }
      }
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

const thirteenHours = 13 * 60 * 60;
export const getSpecPageStaticProps: (
  // Needs to be passed as argument instead of import to prevent node libraries attempting to be imported on client side
  getPlaiceholder: (src: string) => Promise<{ base64: string }>,
) => GetStaticProps =
  (getPlaiceholder) =>
  async ({ params }) => {
    configureSentryScope(params);

    if (
      !params ||
      !params.gameSlug ||
      !params.specSlug ||
      !params.classSlug ||
      !params.zoneTypeSlug ||
      !params.difficultySlug ||
      !params.encounterSlug
    ) {
      return {
        notFound: true,
      };
    }

    const slugs = {
      gameSlug: params.gameSlug as string,
      specSlug: params.specSlug as string,
      classSlug: params.classSlug as string,
      zoneTypeSlug: params.zoneTypeSlug as string,
      categorySlug: params.categorySlug as string,
      difficultySlug: params.difficultySlug as string,
      encounterSlug: params.encounterSlug as string,
      affixesSlug: (params.affixesSlug as string) ?? null,
    };

    if (
      slugs.gameSlug === 'classic-wrath' ||
      slugs.gameSlug === 'classic-cata'
    ) {
      return {
        redirect: {
          statusCode: 307,
          destination: `/classic-mop/builds/${params.specSlug}/${
            params.classSlug
          }/raid/${params.categorySlug}/${params.difficultySlug}/all-bosses${
            params.affixesSlug ? `/${params.affixesSlug}` : ''
          }`,
        },
      };
    }

    if (
      slugs.affixesSlug === 'all-affixes' ||
      slugs.affixesSlug === 'last-week'
    ) {
      return {
        redirect: {
          statusCode: 307,
          destination: './this-week',
        },
      };
    }

    if (slugs.difficultySlug === '20') {
      return {
        redirect: {
          statusCode: 307,
          destination: `/${params.gameSlug}/builds/${params.specSlug}/${params.classSlug}/${params.zoneTypeSlug}/${params.categorySlug}/10/${params.encounterSlug}/${params.affixesSlug}`,
        },
      };
    }

    initRequest({
      route: params.affixesSlug
        ? '/[gameSlug]/builds/[specSlug]/[classSlug]/[zoneTypeSlug]/[categorySlug]/[difficultySlug]/[encounterSlug]/[affixesSlug]'
        : '/[gameSlug]/builds/[specSlug]/[classSlug]/[zoneTypeSlug]/[categorySlug]/[difficultySlug]/[encounterSlug]',
      path: `/${params.gameSlug}/builds/${params.specSlug}/${
        params.classSlug
      }/${params.zoneTypeSlug}/${params.categorySlug}/${
        params.difficultySlug
      }/${params.encounterSlug}${
        params.affixesSlug ? `/${params.affixesSlug}` : ''
      }`,
    });

    const translationKeys = [
      'global.show-more',
      'global.show-less',
      'global.jump_to',
      'reports.normalized_to_cast_x_of_ability',
      'global.fullscreen',
      'global.exit_fullscreen',
      'reports.loading_data',
    ];

    const pageViewModels = await getBuildsSpecPageViewModels(
      slugs,
      translationKeys,
    );

    if (!pageViewModels.buildsSpecPage) {
      // handle the high-keys bracket not being present sometimes
      if (slugs.difficultySlug === 'high-keys') {
        return {
          redirect: {
            statusCode: 307,
            destination: `/${params.gameSlug}/builds/${params.specSlug}/${params.classSlug}/${params.zoneTypeSlug}/${params.categorySlug}/10/${params.encounterSlug}/${params.affixesSlug}`,
          },
        };
      }

      // handle old bookmarks and search results
      if (slugs.gameSlug.length) {
        return {
          redirect: {
            statusCode: 307,
            destination: `/${slugs.gameSlug}`,
          },
        };
      }

      return {
        notFound: true,
      };
    }

    return {
      props: {
        googleAnalytics: pageViewModels.googleAnalytics,
        adPlacements: pageViewModels.adPlacements,
        playwireAds: pageViewModels.playwireAds,
        game: pageViewModels.game,
        translations: pageViewModels.translations,
        header: pageViewModels.header,
        footer: pageViewModels.footer,
        page: {
          ...pageViewModels.buildsSpecPage,
          imageBlurDataUrl: pageViewModels.buildsSpecPage.imageUrl
            ? (await getPlaiceholder(pageViewModels.buildsSpecPage.imageUrl))
                .base64
            : null,
        },
      },
      revalidate: thirteenHours,
    };
  };

function sendLoadEvent(parameters: {
  gameSlug: string | null;
  specId: number;
  zoneTypeSlug: string;
  categorySlug: string;
  difficultySlug: string;
  encounterSlug: string;
  affixesSlug: string | null;
}) {
  sendEvent({
    analyticsComponentName: 'BuildsSpecPage',
    eventName: 'Load',
    isInteraction: false,
    extraParameters: {
      game: parameters.gameSlug,
      filter_1: parameters.specId,
      zone_id: parameters.zoneTypeSlug,
      filter_2: parameters.categorySlug,
      difficulty: parameters.difficultySlug,
      encounter_id: parameters.encounterSlug,
      filter_3: parameters.affixesSlug,
    },
  });
}
